import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, XCircle, AlertTriangle, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { BenchmarkRangeBar } from '@/components/shared/BenchmarkRangeBar';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageHeader } from '@/components/shared/PageHeader';
import { Shield } from 'lucide-react';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/client';

const STATUS_CONFIG = {
  within: { color: 'text-green-600 dark:text-green-400', bg: 'bg-green-500/10', icon: CheckCircle2, label: 'Within Range' },
  warning: { color: 'text-yellow-600 dark:text-yellow-400', bg: 'bg-yellow-500/10', icon: AlertTriangle, label: 'Near Boundary' },
  below: { color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-500/10', icon: AlertTriangle, label: 'Below Range' },
  above: { color: 'text-red-600 dark:text-red-400', bg: 'bg-red-500/10', icon: XCircle, label: 'Above Range' },
};

export default function ComplianceCheckerPage() {
  const [selectedClientId, setSelectedClientId] = useState<string>('');

  // Get client list
  const { data: clientsData } = useQuery({
    queryKey: ['clients-for-compliance'],
    queryFn: async () => {
      const r = await apiClient.get('/clients?limit=500');
      return r.data.data;
    },
  });
  const clients = clientsData?.clients || clientsData || [];

  // Get compliance check for selected client
  const { data: checkData, isLoading, error: checkError } = useQuery({
    queryKey: ['compliance-check', selectedClientId],
    queryFn: async () => {
      const r = await apiClient.get(`/compliance/check/${selectedClientId}`);
      return r.data.data;
    },
    enabled: !!selectedClientId,
  });

  const checks = checkData?.checks || [];
  const xero = checkData?.xeroData;
  const benchmark = checkData?.benchmark;

  // Range-band chart rows - same shape + component as the client app, so both
  // views render identical benchmark items and the same chart.
  const benchmarkRows = checks
    .filter((c: any) => c.range && c.range.min !== undefined && c.range.max !== undefined)
    .map((c: any) => ({
      label: c.label || c.metric,
      yourPct: c.yourPct ?? c.clientValue ?? 0,
      range: c.range,
      higherIsBetter: c.higherIsBetter ?? false,
      verdict: c.verdict || 'in-range',
    }));

  // Generate AI insights
  const generateInsights = () => {
    if (!checks.length || !xero) return [];
    const insights: { type: 'success' | 'warning' | 'danger' | 'info'; title: string; detail: string }[] = [];
    const expenseRatio = xero.totalExpenses > 0 && xero.turnover > 0 ? (xero.totalExpenses / xero.turnover * 100) : 0;
    const cosRatio = xero.costOfSales > 0 && xero.turnover > 0 ? (xero.costOfSales / xero.turnover * 100) : 0;
    const labourRatio = xero.labour > 0 && xero.turnover > 0 ? (xero.labour / xero.turnover * 100) : 0;
    const motorRatio = xero.motorVehicle > 0 && xero.turnover > 0 ? (xero.motorVehicle / xero.turnover * 100) : 0;
    const netProfit = xero.turnover - xero.totalExpenses;
    const netMargin = xero.turnover > 0 ? (netProfit / xero.turnover * 100) : 0;

    // Overall compliance assessment
    const outsideRange = checks.filter((c: any) => c.status === 'above' || c.status === 'below');
    const withinRange = checks.filter((c: any) => c.status === 'within');
    if (outsideRange.length === 0) {
      insights.push({ type: 'success', title: 'All ratios within ATO benchmark ranges', detail: `${checkData?.client?.display_name} is fully compliant with all ${checks.length} ATO benchmark metrics for the ${benchmark?.industry} industry. This significantly reduces audit risk.` });
    } else {
      insights.push({ type: 'danger', title: `${outsideRange.length} of ${checks.length} ratios outside ATO benchmarks`, detail: `This may trigger ATO attention. Businesses that fall outside benchmark ranges are more likely to be selected for review or audit. Immediate review recommended.` });
    }

    // Expense ratio analysis
    const expCheck = checks.find((c: any) => c.metric.toLowerCase().includes('total expenses') && !c.metric.toLowerCase().includes('average'));
    if (expCheck) {
      if (expCheck.status === 'above') {
        insights.push({ type: 'danger', title: `Total expenses ratio is ${expCheck.clientValue}% - above benchmark (${expCheck.benchmarkRaw})`, detail: `Your client's expenses are ${expCheck.deviation}% above the upper benchmark limit. The ATO may suspect inflated deductions or overclaimed expenses. Review all expense categories for legitimacy, particularly large or unusual claims. Consider whether any personal expenses have been incorrectly claimed as business expenses.` });
      } else if (expCheck.status === 'below') {
        insights.push({ type: 'warning', title: `Total expenses ratio is ${expCheck.clientValue}% - below benchmark (${expCheck.benchmarkRaw})`, detail: `Unusually low expenses compared to industry peers may indicate unreported income (cash economy concerns) or that the business is not claiming all legitimate deductions. The ATO uses low expense ratios as a potential indicator of cash economy activity. Ensure all income is being reported and all eligible deductions are being claimed.` });
      } else if (expCheck.status === 'within') {
        insights.push({ type: 'success', title: `Expense ratio of ${expCheck.clientValue}% is within the benchmark range`, detail: `This is the key ATO benchmark metric. Being within range means the business expense pattern is consistent with industry peers, reducing audit risk significantly.` });
      }
    }

    // Net profit margin
    if (netMargin > 0) {
      insights.push({ type: 'info', title: `Net profit margin: ${netMargin.toFixed(1)}%`, detail: `The business generated $${netProfit.toLocaleString()} net profit on $${xero.turnover.toLocaleString()} turnover in FY${xero.fy}. ${netMargin > 30 ? 'This is a strong margin indicating efficient operations.' : netMargin > 15 ? 'This is a healthy margin for the industry.' : netMargin > 5 ? 'This is a modest margin. Consider reviewing cost structures for optimization opportunities.' : 'This is a thin margin. The business may benefit from revenue growth strategies or cost reduction measures.'}` });
    }

    // Labour analysis
    const labourCheck = checks.find((c: any) => c.metric.toLowerCase().includes('labour') || c.metric.toLowerCase().includes('labor'));
    if (labourCheck && labourCheck.status !== 'within') {
      if (labourCheck.status === 'above') {
        insights.push({ type: 'warning', title: `Labour costs at ${labourCheck.clientValue}% of turnover - above industry average`, detail: `High labour costs may indicate overstaffing, above-market wages, or inclusion of non-arm's length payments (e.g., payments to related parties). The ATO scrutinizes labour claims particularly in industries with high cash economy risk. Ensure all PAYG withholding and superannuation obligations are met.` });
      } else if (labourCheck.status === 'below') {
        insights.push({ type: 'info', title: `Labour costs at ${labourCheck.clientValue}% - below industry peers`, detail: `Lower than expected labour costs may indicate use of subcontractors (check contractor vs employee classification), unpaid family labour, or efficient automation. If using subcontractors, ensure correct ABN and TPAR reporting.` });
      }
    }

    // Cost of sales analysis
    const cosCheck = checks.find((c: any) => c.metric.toLowerCase().includes('cost of sales'));
    if (cosCheck && cosCheck.status !== 'within') {
      if (cosCheck.status === 'above') {
        insights.push({ type: 'warning', title: `Cost of sales at ${cosCheck.clientValue}% - above benchmark`, detail: `Higher than expected COGS may indicate: pricing pressure, supplier cost increases, inventory shrinkage, or private use of trading stock not being accounted for. Review supplier contracts and pricing strategies.` });
      }
    }

    // Motor vehicle analysis
    const motorCheck = checks.find((c: any) => c.metric.toLowerCase().includes('motor vehicle'));
    if (motorCheck && motorCheck.status === 'above') {
      insights.push({ type: 'danger', title: `Motor vehicle expenses at ${motorCheck.clientValue}% - ${motorCheck.deviation}% above benchmark`, detail: `Motor vehicle claims are one of the ATO's top audit targets. Ensure logbooks are maintained for the requisite 12-week period, private use is correctly apportioned, and luxury car tax limits are observed. Consider whether the cents-per-kilometre method may be more appropriate for some vehicles.` });
    }

    // Turnover context
    insights.push({ type: 'info', title: `Turnover of $${xero.turnover.toLocaleString()} places this client in the "${benchmark?.turnoverRange}" bracket`, detail: `ATO benchmarks vary significantly by turnover range. Higher turnover businesses typically have higher expense ratios due to economies of scale, employee costs, and overheads. The benchmarks for this range are derived from ${benchmark?.industry} businesses with similar revenue.` });

    return insights;
  };

  const insights = checkData?.status === 'checked' ? generateInsights() : [];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Shield}
        title="ATO Compliance Checker"
        description="Compare client financials against ATO industry benchmarks"
      />


      {/* Client Selector */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium whitespace-nowrap">Select Client:</label>
            <Select value={selectedClientId} onValueChange={setSelectedClientId}>
              <SelectTrigger className="w-[350px]">
                <SelectValue placeholder="Choose a client to check..." />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {clients.map((c: any) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.displayName || c.display_name} {c.industry ? `(${c.industry})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {isLoading && (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          <span className="ml-3 text-sm text-muted-foreground">Fetching accounting data and comparing against benchmarks...</span>
        </div>
      )}

      {checkData?.status === 'no_industry' && (
        <Card className="border-yellow-200">
          <CardContent className="pt-4 pb-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            <div>
              <p className="text-sm font-medium">Industry not set</p>
              <p className="text-xs text-muted-foreground">{checkData.message}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {checkData?.status === 'no_benchmark' && (
        <Card className="border-yellow-200">
          <CardContent className="pt-4 pb-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            <p className="text-sm">{checkData.message}</p>
          </CardContent>
        </Card>
      )}

      {checkData?.status === 'no_xero_data' && (
        <Card className="border-blue-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3 mb-3">
              <AlertTriangle className="h-5 w-5 text-blue-500" />
              <p className="text-sm font-medium">{checkData.message}</p>
            </div>
            {benchmark && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Benchmark available for: <strong>{benchmark.industry}</strong></p>
                {benchmark.source && (
                  <a href={benchmark.source} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                    View ATO Benchmark <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {checkData?.status === 'checked' && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card>
              <CardContent className="pt-3 pb-3">
                <p className="text-[10px] text-muted-foreground uppercase">Industry</p>
                <p className="text-sm font-semibold">{benchmark?.industry}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-3 pb-3">
                <p className="text-[10px] text-muted-foreground uppercase">Turnover ({xero?.fy})</p>
                <p className="text-sm font-semibold">${xero?.turnover?.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-3 pb-3">
                <p className="text-[10px] text-muted-foreground uppercase">Total Expenses</p>
                <p className="text-sm font-semibold">${xero?.totalExpenses?.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-3 pb-3">
                <p className="text-[10px] text-muted-foreground uppercase">Turnover Range</p>
                <p className="text-sm font-semibold">{benchmark?.turnoverRange}</p>
              </CardContent>
            </Card>
          </div>

          {/* Benchmark Link */}
          {benchmark?.source && (
            <a href={benchmark.source} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline">
              <ExternalLink className="h-3 w-3" /> View ATO Benchmark for {benchmark.industry}
            </a>
          )}

          {/* Chart - same range-band visual as the client app */}
          {benchmarkRows.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Client vs ATO Benchmark Ranges</CardTitle>
                <CardDescription className="text-xs">
                  Each bar shows the typical ATO range for this industry and turnover bracket, with your client's value marked by a dot.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {benchmarkRows.map((row: any) => (
                  <BenchmarkRangeBar key={row.label} row={row} />
                ))}
              </CardContent>
            </Card>
          )}

          {/* AI Insights */}
          {insights.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Financial Analyst Insights</CardTitle>
                <CardDescription className="text-xs">Expert analysis based on ATO benchmarks and financial ratios</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {insights.map((insight, i) => (
                  <div key={i} className={cn(
                    'rounded-lg p-3 border-l-4',
                    insight.type === 'success' ? 'border-l-green-500 bg-green-500/5' :
                    insight.type === 'warning' ? 'border-l-yellow-500 bg-yellow-500/5' :
                    insight.type === 'danger' ? 'border-l-red-500 bg-red-500/5' :
                    'border-l-blue-500 bg-blue-500/5'
                  )}>
                    <p className={cn('text-xs font-semibold mb-1',
                      insight.type === 'success' ? 'text-green-600 dark:text-green-400' :
                      insight.type === 'warning' ? 'text-yellow-600 dark:text-yellow-400' :
                      insight.type === 'danger' ? 'text-red-600 dark:text-red-400' :
                      'text-blue-600 dark:text-blue-400'
                    )}>
                      {insight.type === 'success' ? '✓' : insight.type === 'danger' ? '✗' : insight.type === 'warning' ? '⚠' : 'ℹ'} {insight.title}
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{insight.detail}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Detailed Checks Table */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Detailed Compliance Checks</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-3 py-2 text-left font-medium">Metric</th>
                    <th className="px-3 py-2 text-left font-medium">Your Client</th>
                    <th className="px-3 py-2 text-left font-medium">ATO Benchmark</th>
                    <th className="px-3 py-2 text-left font-medium">Status</th>
                    <th className="px-3 py-2 text-left font-medium">Deviation</th>
                  </tr>
                </thead>
                <tbody>
                  {checks.map((check: any, i: number) => {
                    const cfg = STATUS_CONFIG[check.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.within;
                    const Icon = cfg.icon;
                    return (
                      <tr key={i} className={cn('border-b', cfg.bg)}>
                        <td className="px-3 py-2 font-medium">
                          {check.metric}
                          {check.isKey && <Badge variant="outline" className="ml-1 text-[9px]">Key</Badge>}
                        </td>
                        <td className={cn('px-3 py-2 font-bold', cfg.color)}>{check.clientValue}%</td>
                        <td className="px-3 py-2 text-muted-foreground">{check.benchmarkRaw}</td>
                        <td className="px-3 py-2">
                          <span className={cn('inline-flex items-center gap-1', cfg.color)}>
                            <Icon className="h-3.5 w-3.5" />
                            {cfg.label}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {check.deviation > 0 ? (
                            <span className={cn('font-medium', check.status === 'above' ? 'text-red-500' : 'text-blue-500')}>
                              {check.deviation}% {check.status === 'above' ? 'over' : 'under'}
                            </span>
                          ) : (
                            <span className="text-green-600">OK</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {checks.length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">No comparison data available</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
